import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import DataTable from "@insite/mobius/DataTable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import { DataTableBody, DataTableCell, DataTableRow } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CENProductDetailSearchSpecificationBar from "@tCentro/Components/CENProductDetailSearchSpecificationBar";
import CENProductDetailsFeedbackModalComponent from "@tCentro/Components/CENProductDetailsFeedbackModalComponent";
import CENpdf from "@tCentro/Icons/CENpdf";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import chunk from "lodash/chunk";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
    attributesPosition = "attributesPosition",
    documentsPosition = "documentsPosition",
}

type Props = WidgetProps & HasProduct;

export interface ProductDetailsSpecificationsStyles {
    specificationsAccordion?: AccordionProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    specAttrdataTableCellCss?: InjectableCss;
    specDataTableCellCss?: InjectableCss;
    dataRowCss?: InjectableCss;
    attributesTitle?: TypographyPresentationProps;
    additionalLink?: TypographyPresentationProps;
    accordionContainer?: InjectableCss;
    feedbackWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    pdfIconStyles?: IconPresentationProps;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    specificationsAccordion: {
        headingLevel: 2,
        css: css`
            margin: 10px 0px 30px 0px;
            dt:first-child {
                button {
                    border-top: 1px solid ${COLORS.disabled.dark} !important;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin: 0px 0px !important;
                        `,
                        css`
                            margin: 0px 0px;
                        `,

                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    specificationsAccordionSection: {
        toggleIconProps: {
            src: Minus,
        },
        expandIconProps: {
            src: Plus,
        },
        headerProps: {
            css: css`
                > button {
                    padding: 15px;
                    padding-left: 0px;
                    border: none !important;
                    border-bottom: 1px solid ${COLORS.disabled.dark} !important;
                    background: none !important;
                    &:focus {
                        padding-top: 0px;
                        padding: 15px !important;
                        padding-left: 0px !important;
                    }
                    span {
                        color: ${getProp("theme.typography.p.color")} !important;
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        line-height: 32px;
                        font-size: ${TYPOGRAPHY.size.largest} !important;
                    }
                    svg {
                        color: #9e9d9e !important;
                        height: 22px;
                        width: 22px;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            > button {
                                span {
                                    line-height: 24px;
                                    font-size: ${TYPOGRAPHY.size.large} !important;
                                }
                            }
                        `,
                        css`
                            > button {
                                span {
                                    line-height: 24px;
                                    font-size: ${TYPOGRAPHY.size.large} !important;
                                }
                            }
                        `,
                        null,
                        null,
                        null,
                    ])}
            `,
        },
        panelProps: {
            css: css`
                border: none !important;
                background: #fff;
                padding-left: 0px;
                padding-right: 0px;
                ul {
                    color: ${getProp("theme.typography.p.color")};
                }
                div {
                    color: ${getProp("theme.typography.p.color")};
                }
            `,
        },
    },
    documentGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            border-bottom: 1px solid #E0DFE0;
            width: 100%;
            padding: 15px 0px;
            margin-left: auto !important;
            margin-right: auto !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                       max-width: 93% !important;
                    `,
                    css`
                        max-width: 93% !important;
                    `,
                    css`
                        max-width: 96% !important;
                    `,
                    css`
                       max-width: 96% !important;
                    `,
                    css`
                       max-width: 98% !important;
                    `,
                ])}
        `
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
            margin-left: 5px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.large};
            line-height: 24px;
        `,
    },
    attributeTypeLabelText: {
        css: css`
            font-weight: 600;
            letter-spacing: 0;
            text-align: justify;
            color: #33333a;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0;
                        font-size: 14px;
                        line-height: 17px;
                    `,
                    css`
                        padding: 0;
                        font-size: 14px;
                        line-height: 17px;
                    `,
                    css`
                        font-size: 14px;
                        line-height: 24px;
                    `,
                    css`
                        font-size: 16px;
                        line-height: 24px;
                    `,
                    css`
                        font-size: 16px;
                        line-height: 24px;
                    `,
                ])}
        `,
    },

    attributeTypeValuesText: {
        css: css`
            color: #33333a;
            font-weight: 300;
            letter-spacing: 0;
            text-align: justify;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 14px;
                        line-height: 17px;
                    `,
                    css`
                        font-size: 14px;
                        line-height: 17px;
                    `,
                    css`
                        font-size: 16px;
                        line-height: 24px;
                    `,
                    css`
                        font-size: 16px;
                        line-height: 24px;
                    `,
                    css`
                        font-size: 16px;
                        line-height: 24px;
                    `,
                ])}
        `,
    },

    attributesTitle: {
        css: css`
            font-family: Poppins;
            font-weight: 600;
            font-size: 24px;
            line-height: 32px;
            color: #000;
            margin: 25px 0px;
            display: flex;
            gap: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    null,
                    null,
                    null,
                ])}
        `
    },

    additionalLink:{
        css:css`
            font-family: Poppins;
            font-weight: 600;
            font-size: 18px;
            line-height: 24px;
            letter-spacing: 0%;
            vertical-align: middle;
            color: #0F96D9;
            cursor: pointer;
            display: flex;
            align-items: center;
        `
    },
    specAttrdataTableCellCss: {
        css: css`
            padding: 0 16px;
            white-space: normal;
            box-sizing: content-box;
            width: 50%;
            height: auto;
            max-width: 300px;
            border: none;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 70%;
                    `,
                    css`
                        width: 70%;
                    `,
                    css`
                        width: 70%;
                    `,
                    css`
                        width: 70%;
                    `,
                    null,
                ])}
        `,
    },

    specDataTableCellCss: {
        css: css`
            white-space: normal;
            border: none;
            height: auto;
            justify-content: end;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0;
                    `,
                    css`
                        padding: 0;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    dataRowCss: {
        css: css`
            display: grid;
            gap: 24px;
            align-items: center;
            background: transparent;
            grid-template-columns: 1fr 1fr;
            & > div{
                width: 50%;
                display: flex;
                justify-content: space-between;
                align-items: center;
                background: transparent;
                width: 100%;
                padding: 15px;
                height: 100%;
                & > td{
                    display: flex;
                    align-items: center;
                }
            }
            :nth-child(odd) {
                & > div {
                    background-color: #fff !important;
                    border-bottom: 1px solid #E0DFE0
                }
            }
            :nth-child(even) {
            background-color: transparent !important;
                & > div {
                    background-color: #F2F2F2 !important;
                    border-bottom: 1px solid #E0DFE0;
                }
            }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display:  flex;   
                    `,
                    css`
                        display:  flex;
                    `,
                    css`
                        display:  flex;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    documentLink: {
        css: css`
            > span{
                display: flex;
                align-items: center; 
            }
        `
    },
    accordionContainer: {
        css: css`
            margin: 15px 0px;
        `
    },
    feedbackWrapper: {
        css: css`
            margin: 20px 0px;
            display: flex;
        `
    },
    titleWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
        `
    },
    pdfIconStyles: {
        css: css`
            position: relative;
            margin-right: 5px;
        `
    }
};

const styles = specificationsStyles;

const ProductDetailsSpecifications: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications },
    fields: { showDocuments, showAttributes, documentsPosition, attributesPosition },
}) => {
    const allAttributTypesObject = chunk(attributeTypes, 3);
    const [allAttributTypes, setAllAttributTypes] = React.useState(allAttributTypesObject);
    const renderingDocuments = showDocuments && documents && documents.length > 0;
    const renderingAttributes = showAttributes && (brand || (attributeTypes && attributeTypes?.length > 0));
    // const renderingSpecifications = specifications && specifications.length > 0;
    const renderingSpecifications = false;
    const documentsFirst = documentsPosition === "first";
    const attributesFirst = attributesPosition === "first";

    const documentRef = React.useRef<HTMLDivElement | null>(null);

    if (!renderingDocuments && !renderingAttributes && !renderingSpecifications) {
        return null;
    }

    React.useEffect(() => {
        const dividedrows = () => {
            if (window.innerWidth > 991) {
                const allAttributTypesObject = chunk(attributeTypes, 2);
                setAllAttributTypes(allAttributTypesObject);
            } else {
                const allAttributTypesObject = chunk(attributeTypes, 1);
                setAllAttributTypes(allAttributTypesObject);
            }
        };
        window.addEventListener("resize", dividedrows);
        dividedrows();
    }, [attributeTypes]);

    const handleScroll = () => {
        if (documentRef.current && typeof window !== "undefined") {
            const header = document.querySelector('[data-test-selector="header"]');
            const headerHeight = header ? header.getBoundingClientRect().height : 0;
            const y = documentRef.current.getBoundingClientRect().top + window.pageYOffset - headerHeight;

            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    };

    const documentsAccordion = renderingDocuments && (
        <StyledWrapper ref={documentRef} {...styles.accordionContainer}>
            <Typography {...styles.attributesTitle} as="p">
                {translate("Documents ")}
            </Typography>
            <GridContainer {...styles.documentsContainer}>
                {documents!.map(
                    document =>
                        document.documentType.toLowerCase() !== "article" && (
                            <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                                <Link
                                    href={document.filePath}
                                    target="_new"
                                    {...styles.documentLink}
                                    data-test-selector={`productDetails_${document.id}`}
                                >
                                     <Icon src={CENpdf} />
                                    <Typography {...styles.documentNameText}>{document.name}</Typography>
                                </Link>
                            </GridItem>
                        ),
                )}
            </GridContainer>
        </StyledWrapper>
    );

    const attributesAccordion = renderingAttributes && attributeTypes?.length !== 0 && (
        <StyledWrapper {...styles.accordionContainer}>
            <Typography {...styles.attributesTitle} as="p">
                {translate("Attributes:")}
                {renderingDocuments && <Typography onClick={handleScroll} {...styles.additionalLink} as="span">
                    <Icon src={CENpdf} {...styles.pdfIconStyles}/>
                    {translate("View Additional Documents ")}
                </Typography>}
            </Typography>
            <DataTable>
                <DataTableBody>
                    {/* {brand && (
                        <DataTableRow>
                            <DataTableCell>
                                <Typography {...styles.attributeTypeLabelText}>{translate("Brand")}:&nbsp;</Typography>
                            </DataTableCell>
                            <DataTableCell>
                                <Typography {...styles.attributeTypeValuesText}>{brand.name}</Typography>
                            </DataTableCell>
                        </DataTableRow>
                    )} */}

                    {allAttributTypes?.map(attributeType => {
                        return (
                            <>
                                <DataTableRow {...styles.dataRowCss}>
                                    {attributeType?.map(
                                        (specification: {
                                            name: string | number | undefined;
                                            label: any;
                                            attributeValues: any;
                                        }) => (
                                            // eslint-disable-next-line react/jsx-key
                                            <StyledWrapper>
                                                <DataTableCell {...styles.specAttrdataTableCellCss}>
                                                    <Typography {...styles.attributeTypeLabelText}>
                                                        {specification.label}:
                                                    </Typography>
                                                </DataTableCell>

                                                <DataTableCell {...styles.specDataTableCellCss}>
                                                    <Typography {...styles.attributeTypeValuesText}>
                                                        {specification.attributeValues
                                                            ?.map(
                                                                (attributeValue: { valueDisplay: any }) =>
                                                                    attributeValue.valueDisplay,
                                                            )
                                                            .join(", ")}
                                                    </Typography>
                                                </DataTableCell>
                                            </StyledWrapper>
                                        ),
                                    )}
                                </DataTableRow>
                            </>
                        );
                    })}
                </DataTableBody>
            </DataTable>
            <StyledWrapper {...styles.feedbackWrapper}>
                <CENProductDetailsFeedbackModalComponent />
            </StyledWrapper>
        </StyledWrapper>
    );
    return (
        <Accordion
            headingLevel={2}
            data-test-selector="productDetails_specifications"
            {...styles.specificationsAccordion}
        >
            {attributesFirst && attributesAccordion}
            {documentsFirst && documentsAccordion}
            {renderingSpecifications &&
                specifications?.map((specification, index) => {
                    return (
                        <ManagedAccordionSection
                            initialExpanded={true}
                            key={specification.id.toString()}
                            title={specification.nameDisplay}
                            data-test-selector="productDetails_specifications_specification"
                            {...styles.specificationsAccordionSection}
                        >
                            {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                        </ManagedAccordionSection>
                    );
                })}
            {!attributesFirst && attributesAccordion}
            {!documentsFirst && documentsAccordion}
        </Accordion>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsSpecifications),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.documentsPosition,
                displayName: "Documents Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showDocuments,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.attributesPosition,
                displayName: "Attributes Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 4,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showAttributes,
            },
        ],
    },
};

export default widgetModule;
